import React, { useState, useEffect, useContext } from 'react';
import { Card, Table, Button } from 'react-bootstrap';
import Icon from '@mdi/react';
import { mdiSquareEditOutline, mdiTrashCanOutline } from '@mdi/js';
import { gstContext1 } from './gstContext';
import { useGST } from './getGST';
import { toast } from "react-toastify";
import { fetchTaxData } from '../../../Utils/function_list';
import { getFromAPI, deleteFromAPI, showAlert, putToAPI, handleDateFormat, formatISODateWithTimezone } from '../../../Utils/utils';
import FormModal from '../../../Utils/FormModal';
import messages from '../../../Utils/lang';
import GSTConfigurationData from "./gst-configuration-data.json";

export default function ViewTax({ setSelectedGSTId }) {
  const initialGstInputData = {
    name: '',
    taxrate: '',
    country_id: 0,
    applicable_date: formatISODateWithTimezone(new Date()),
    iscombined: 1,
    child_ids: 'NA',
    isdefault: 0,
    type: 2,
    description: 'NA',
    created_branch_id: 0,
    isdeleted: 0,
    created_user_id: 0,
    last_modified_user_id: 0,
    last_modified_date_time: formatISODateWithTimezone(new Date()),
    created_date_time: formatISODateWithTimezone(new Date()),
    reserve_varchar1: 'NA',
    reservs_varchar2: 'NA',
    reserve_float1: 0.0,
    reserve_float2: 0.0,
    reserve_int1: 0,
    reserve_int2: 0,
    reserve_double1: 0.0,
    reserve_date1: '2024-09-18',
  };

  const [gstInputData, setGstInputData] = useState(initialGstInputData);
  const [tableData, setTableData] = useState([]);
  const [taxType, setTaxType] = useState('simple');
  const [enterTaxRate1, setEnterTaxRate1] = useState('');
  const [enterTaxRate2, setEnterTaxRate2] = useState('');
  const [Tax, setTax] = useState([]);
  const [editTaxModalShow, setShowFormModal] = useState(false);
  const [gstConfigurationTableData] = useState(GSTConfigurationData);

  const selectedGSTId = useContext(gstContext1);
  const { gstData, fetchGSTData } = useGST();

  const handleCancel = () => {
    setGstInputData(initialGstInputData);
    setTaxType('simple');
    setEnterTaxRate1('');
    setEnterTaxRate2('');
  };

  useEffect(() => {
    if (selectedGSTId) {
      fetchGSTData();
      fetchUnitDetails();
      fetchUnitTable();
    }
  }, [selectedGSTId]);

  const fetchUnitDetails = async () => {
    try {
      const response = await getFromAPI(`FtsTaxes/?id=${selectedGSTId}`);
      if (response && response.length > 0) {
        const fetchedData = response[0];
        const childIdsArray = fetchedData.child_ids ? fetchedData.child_ids.split(',') : [];
        const taxRate1 = childIdsArray[0] || '';
        const taxRate2 = childIdsArray[1] || '';

        setGstInputData({
          ...initialGstInputData,
          name: fetchedData.name,
          taxrate: fetchedData.taxrate,
          type: fetchedData.type,
          child_ids: fetchedData.child_ids,
          iscombined: fetchedData.iscombined,
          isdefault: fetchedData.isdefault,
          created_branch_id: fetchedData.created_branch_id,
          applicable_date: handleDateFormat(fetchedData.applicable_date),
          isdeleted: fetchedData.isdeleted,
          description: fetchedData.description, // Preserve fetched description
        });

        setTaxType(fetchedData.iscombined === 1 ? 'simple' : 'combine');
        setEnterTaxRate1(taxRate1);
        setEnterTaxRate2(taxRate2);
      }
    } catch (error) {
      console.error('Error fetching tax details:', error);
    }
  };

  const fetchUnitTable = async () => {
    try {
      const response = await getFromAPI(`FtsTaxes/?id=${selectedGSTId}`);
      if (response && response.length > 0) {
        const data = response[0];
        const responseType = data.type === 1 ? 'Inclusive' : data.type === 2 ? 'Exclusive' : 'Inclusive';
        const iscombinedData = data.iscombined === 2 ? 'True' : 'False';

        const mappedData = [
          { tableheading1: "Tax Name :", tablesubtext1: data.name },
          { tableheading1: "Tax Rate :", tablesubtext1: data.taxrate },
          { tableheading1: "Combine Tax:", tablesubtext1: iscombinedData },
          { tableheading1: "Combine Tax Detail :", tablesubtext1: data.description || 'NA' },
          { tableheading1: "Default Tax :", tablesubtext1: data.isdefault === 1 ? 'Yes' : 'No' },
          { tableheading1: "Tax Type :", tablesubtext1: responseType },
        ];

        setTableData(mappedData);
      }
    } catch (error) {
      console.error('Error fetching table data:', error);
    }
  };

  useEffect(() => {
    fetchTaxData(setTax);
  }, []);

  // Helper function to generate description from tax rates
  const generateDescription = (taxRate1, taxRate2) => {
    const names = [taxRate1, taxRate2].map(id => {
      const tax = Tax.find(t => t.id === parseInt(id));
      return tax ? tax.name : 'NA';
    }).filter(Boolean);
    return names.length > 0 ? names.join(' + ') : 'NA';
  };

  const handleEditTaxChange = (arg1, arg2, arg3) => {
    if (arg1 && arg1.target) {
      const { name, value, type, checked } = arg1.target;
      if (type === 'checkbox') {
        setGstInputData(prev => ({ ...prev, [name]: checked ? 1 : 0 }));
      } else if (name === 'taxType') {
        setTaxType(value);
        setGstInputData(prev => ({
          ...prev,
          iscombined: value === 'simple' ? 1 : 2,
          child_ids: value === 'simple' ? '' : prev.child_ids,
          taxrate: value === 'combine' ? '' : prev.taxrate,
          description: value === 'combine' ? generateDescription(enterTaxRate1, enterTaxRate2) : 'NA',
        }));
      } else if (name === 'enterTaxRate1') {
        setEnterTaxRate1(value);
        if (taxType === 'combine') {
          const newChildIds = [value, enterTaxRate2].filter(Boolean).join(',');
          setGstInputData(prev => ({
            ...prev,
            child_ids: newChildIds,
            description: generateDescription(value, enterTaxRate2),
          }));
        }
      } else if (name === 'enterTaxRate2') {
        setEnterTaxRate2(value);
        if (taxType === 'combine') {
          const newChildIds = [enterTaxRate1, value].filter(Boolean).join(',');
          setGstInputData(prev => ({
            ...prev,
            child_ids: newChildIds,
            description: generateDescription(enterTaxRate1, value),
          }));
        }
      } else {
        setGstInputData(prev => ({
          ...prev,
          [name]: name === 'type' ? parseInt(value) : value,
        }));
      }
    } else {
      const field = arg2;
      const value = arg3;

      if (field === 'taxType') {
        setTaxType(value);
        setGstInputData(prev => ({
          ...prev,
          iscombined: value === 'simple' ? 1 : 2,
          child_ids: value === 'simple' ? '' : prev.child_ids,
          taxrate: value === 'combine' ? '' : prev.taxrate,
          description: value === 'combine' ? generateDescription(enterTaxRate1, enterTaxRate2) : 'NA',
        }));
      } else if (field === 'enterTaxRate1') {
        setEnterTaxRate1(value);
        if (taxType === 'combine') {
          const newChildIds = [value, enterTaxRate2].filter(Boolean).join(',');
          setGstInputData(prev => ({
            ...prev,
            child_ids: newChildIds,
            description: generateDescription(value, enterTaxRate2),
          }));
        }
      } else if (field === 'enterTaxRate2') {
        setEnterTaxRate2(value);
        if (taxType === 'combine') {
          const newChildIds = [enterTaxRate1, value].filter(Boolean).join(',');
          setGstInputData(prev => ({
            ...prev,
            child_ids: newChildIds,
            description: generateDescription(enterTaxRate1, value),
          }));
        }
      } else if (field === 'isdefault') {
        setGstInputData(prev => ({ ...prev, [field]: value === '1' ? 1 : 0 }));
      } else {
        setGstInputData(prev => ({
          ...prev,
          [field]: field === 'type' ? parseInt(value) : value,
        }));
      }
    }
  };

  const handleSaveChanges = async () => {
    if (!gstInputData.name.trim()) {
      toast.error(messages.gst.required_name, 'error');
      return;
    }
    if (!gstInputData.taxrate.trim() && taxType === 'simple') {
      toast.error(messages.gst.required_tax_rate, 'error');
      return;
    }

    const isExistsName = Array.isArray(gstData) && gstData.find(gst =>
      gst.name === gstInputData.name && gst.id !== selectedGSTId
    );
    if (isExistsName) {
      toast.error(messages.gst.already_exist);
      return;
    }

    // Ensure description is updated before saving
    const updatedGstInputData = {
      ...gstInputData,
      description: taxType === 'combine' ? generateDescription(enterTaxRate1, enterTaxRate2) : 'NA',
    };

    try {
      await putToAPI(`FtsTaxes/${selectedGSTId}/`, updatedGstInputData);
      toast.success('GST details updated successfully', 'success');
      setShowFormModal(false);
      fetchGSTData();
      fetchUnitTable();
    } catch (error) {
      console.error('Error updating GST details:', error);
      toast.error('Failed to update GST details', 'error');
    }
  };

  const handleDelete = async (itemId) => {
    try {
      await deleteFromAPI(`FtsTaxes/${itemId}/`);
      toast.success('GST deleted successfully', 'success');
      fetchGSTData();
      fetchUnitTable();
      setSelectedGSTId(gstData.length > 0 ? gstData[0].id : '');
    } catch (error) {
      toast.error('GST not deleted', 'error');
    }
  };

  const handleDeleteGST = () => {
    showAlert('Are you sure you want to delete this GST?', 'confirm', (result) => {
      if (result) handleDelete(selectedGSTId);
    });
  };

  const editTaxRows = [
    {
      columns: [
        { size: { lg: 8 }, label: 'Tax Name', type: 'text', name: 'name', id: 'name', value: gstInputData.name, placeholder: 'Enter tax name...' },
        {
          size: { xl: 4 }, type: 'radios', class: 'text-xl-center mt-xl-4', value: taxType, name: 'taxType',
          options: [
            { value: 'simple', label: 'Simple Tax', id: 'simple-tax-check', checked: taxType === 'simple' },
            { value: 'combine', label: 'Combine Tax', id: 'combine-tax-check', checked: taxType === 'combine' },
          ]
        },
        { size: { xl: 4 }, label: 'Tax Rate', type: 'text', name: 'taxrate', id: 'taxrate', value: gstInputData.taxrate, placeholder: 'Enter tax rate...' },
        {
          size: { md: 6, xl: 4 }, label: 'Tax Rate 1', type: 'select', name: 'enterTaxRate1', id: 'enterTaxRate1', value: enterTaxRate1, disabled: taxType === 'simple',
          options: [{ value: '', label: 'Select Tax Rate 1' }, ...Tax.map(item => ({ value: item.id, label: item.name }))] },
        {
          size: { md: 6, xl: 4 }, label: 'Tax Rate 2', type: 'select', name: 'enterTaxRate2', id: 'enterTaxRate2', value: enterTaxRate2, disabled: taxType === 'simple',
          options: [{ value: '', label: 'Select Tax Rate 2' }, ...Tax.map(item => ({ value: item.id, label: item.name }))] },
        {
          size: { xl: 4 }, type: 'radios', value: parseInt(gstInputData.type), name: 'type',
          options: [
            { value: 1, label: 'Inclusive', id: 'inclusive-check', checked: parseInt(gstInputData.type) === 1 },
            { value: 2, label: 'Exclusive', id: 'exclusive-check', checked: parseInt(gstInputData.type) === 2 },
          ]
        },
        {
          size: { sm: 12 }, type: 'checkboxes', value: gstInputData.isdefault, name: 'isdefault',
          checkboxes: [{ value: '1', id: 'isdefault', checked: gstInputData.isdefault === 1, label: 'Set this tax as default tax.' }]
        },
      ]
    }
  ];

  const buttonsEditTax = [
    { variant: "primary", text: "Update", onClick: handleSaveChanges },
    { variant: "secondary", text: "Cancel", onClick: () => setShowFormModal(false) },
  ];

  return (
    <>
      <Card className='right-content-details'>
        <Card.Body>
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <Card.Title className='mb-0'>View Tax</Card.Title>
            <div>
              <Button variant="soft-info p-0 me-3" onClick={() => setShowFormModal(true)} disabled={!selectedGSTId}>
                <div className='avatar avatar-sm'>
                  <Icon path={mdiSquareEditOutline} className='avatar-icon' />
                </div>
              </Button>
              <Button variant="soft-danger p-0" onClick={handleDeleteGST} disabled={!selectedGSTId}>
                <div className='avatar avatar-sm'>
                  <Icon path={mdiTrashCanOutline} className='avatar-icon' />
                </div>
              </Button>
            </div>
          </div>

          <Table responsive className='table-lg border table-nowrap mb-0'>
            <tbody>
              {tableData.map((data, i) => (
                <tr key={i}>
                  <th>{data.tableheading1}</th>
                  <td>{data.tablesubtext1}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <FormModal
        title="Edit Tax"
        formModalShow={editTaxModalShow}
        Buttons={buttonsEditTax}
        handleFormModalClose={() => setShowFormModal(false)}
        rows={editTaxRows}
        handleInputChange={handleEditTaxChange}
        handleSubmit={(e) => e.preventDefault()}
      />
    </>
  );
}